import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, FileText, Calendar } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const ReportsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/compliance?period_start=${dateRange.start}&period_end=${dateRange.end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/compliance/pdf?period_start=${dateRange.start}&period_end=${dateRange.end}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapport_conformite_${dateRange.start}_${dateRange.end}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download PDF:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-primary mb-3 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au tableau de bord
        </button>

        <h1 className="text-2xl md:text-3xl font-bold text-primary-900 font-heading mb-6 md:mb-8">
          Rapports de conformité
        </h1>

        <Card className="mb-6 md:mb-8">
          <CardHeader>
            <CardTitle className="text-base md:text-lg">Générer un rapport</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Date de début
                  </label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Date de fin
                  </label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  />
                </div>
              </div>
              <Button onClick={generateReport} disabled={loading}>
                <FileText className="w-5 h-5 mr-2" />
                {loading ? 'Génération...' : 'Générer le rapport'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {reportData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <CardTitle className="text-base md:text-lg">Rapport généré</CardTitle>
                  <Button onClick={downloadPDF} variant="secondary" className="w-full sm:w-auto">
                    <Download className="w-5 h-5 mr-2" />
                    Télécharger PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-6">
                  <div className="p-3 md:p-4 bg-primary-50 rounded-2xl">
                    <p className="text-xs md:text-sm text-slate-600 mb-1">Score de conformité</p>
                    <p className="text-xl md:text-3xl font-bold text-primary-900">{reportData.compliance_score.toFixed(1)}%</p>
                  </div>
                  <div className="p-3 md:p-4 bg-secondary-50 rounded-2xl">
                    <p className="text-xs md:text-sm text-slate-600 mb-1">Bénéficiaires</p>
                    <p className="text-xl md:text-3xl font-bold text-primary-900">{reportData.total_members}</p>
                  </div>
                  <div className="p-3 md:p-4 bg-accent/20 rounded-2xl">
                    <p className="text-xs md:text-sm text-slate-600 mb-1">Notes rédigées</p>
                    <p className="text-xl md:text-3xl font-bold text-primary-900">{reportData.total_notes}</p>
                  </div>
                  <div className="p-3 md:p-4 bg-slate-100 rounded-2xl">
                    <p className="text-xs md:text-sm text-slate-600 mb-1">Taux de complétion</p>
                    <p className="text-xl md:text-3xl font-bold text-primary-900">{reportData.task_completion_rate.toFixed(1)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
